import * as THREE from 'three';
import hullPanelsUrl from '@/assets/ships/hull-panels.png?url';
import heatshieldTilesUrl from '@/assets/ships/heatshield-tiles.png?url';
import { Vec3 } from '@/core/math/Vec3';
import { Engine } from '@/spacecraft/Propulsion';
import { EngineDef, ShipDef } from '@/spacecraft/ShipDef';

type Disposable = THREE.BufferGeometry | THREE.Material | THREE.Texture;

interface ShipMaterials {
  hull: THREE.MeshStandardMaterial;
  heatshield: THREE.MeshStandardMaterial;
  structure: THREE.MeshStandardMaterial;
  canopy: THREE.MeshStandardMaterial;
  accent: THREE.MeshStandardMaterial;
  radiator: THREE.MeshStandardMaterial;
  nozzle: THREE.MeshStandardMaterial;
  intake: THREE.MeshStandardMaterial;
  landingLight: THREE.MeshStandardMaterial;
  portLight: THREE.MeshStandardMaterial;
  starboardLight: THREE.MeshStandardMaterial;
}

interface HullRing {
  z: number;
  radiusX: number;
  radiusY: number;
}

/**
 * Procedural spacecraft model with thrust effects.
 *
 * The silhouette is still derived from the physical ship definition, but it
 * is assembled as a believable lifting body rather than a capsule with boxes
 * attached. Generated hull and heat-shield maps provide material-scale detail
 * while the large forms (sweep, taper, nacelles and landing gear) remain real
 * geometry and read correctly at any distance.
 *
 * Engine plumes are driven by the actual thrust each engine is producing. A
 * browned-out engine shows nothing and a damaged or gimballed engine's plume
 * follows its real thrust vector.
 */
export class ShipModel {
  readonly root = new THREE.Group();
  private readonly plumes = new Map<string, THREE.Mesh>();
  private readonly plumeMaterials = new Map<string, THREE.MeshBasicMaterial>();
  private readonly thrustDirectionScratch = new Vec3();
  private readonly disposables: Disposable[] = [];
  private readonly materials: ShipMaterials;
  private fieldGlow: THREE.Mesh | null = null;

  constructor(private readonly def: ShipDef) {
    this.root.name = `${def.id}-ship-model`;
    this.materials = this.buildMaterials();
    this.buildHull();
    this.buildEngines();
    this.buildFieldGlow();
    this.root.frustumCulled = false;
  }

  private track<T extends Disposable>(value: T): T {
    this.disposables.push(value);
    return value;
  }

  private loadTexture(url: string, repeatX: number, repeatY: number): THREE.Texture {
    const texture = this.track(new THREE.TextureLoader().load(url));
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(repeatX, repeatY);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.anisotropy = 8;
    return texture;
  }

  private buildMaterials(): ShipMaterials {
    const hullTexture = this.loadTexture(hullPanelsUrl, 3, 4);
    const heatshieldTexture = this.loadTexture(heatshieldTilesUrl, 4, 4);

    return {
      hull: this.track(
        new THREE.MeshStandardMaterial({
          color: 0xd7dce0,
          map: hullTexture,
          roughness: 0.58,
          metalness: 0.56,
        }),
      ),
      heatshield: this.track(
        new THREE.MeshStandardMaterial({
          color: 0xffffff,
          map: heatshieldTexture,
          roughness: 0.9,
          metalness: 0.12,
        }),
      ),
      structure: this.track(
        new THREE.MeshStandardMaterial({ color: 0x343b42, roughness: 0.72, metalness: 0.62 }),
      ),
      canopy: this.track(
        new THREE.MeshStandardMaterial({
          color: 0x07131d,
          roughness: 0.13,
          metalness: 0.62,
          emissive: 0x0a2232,
          emissiveIntensity: 0.48,
        }),
      ),
      accent: this.track(
        new THREE.MeshStandardMaterial({ color: 0xc66b2f, roughness: 0.54, metalness: 0.5 }),
      ),
      radiator: this.track(
        new THREE.MeshStandardMaterial({
          color: 0x182734,
          roughness: 0.34,
          metalness: 0.76,
          emissive: 0x061018,
          emissiveIntensity: 0.18,
        }),
      ),
      nozzle: this.track(
        new THREE.MeshStandardMaterial({ color: 0x24282c, roughness: 0.38, metalness: 0.9, side: THREE.DoubleSide }),
      ),
      intake: this.track(
        new THREE.MeshStandardMaterial({ color: 0x090c0f, roughness: 0.86, metalness: 0.28 }),
      ),
      landingLight: this.track(
        new THREE.MeshStandardMaterial({
          color: 0xe7f4ff,
          emissive: 0xb9dcff,
          emissiveIntensity: 2.4,
          roughness: 0.18,
          metalness: 0.1,
        }),
      ),
      portLight: this.track(
        new THREE.MeshStandardMaterial({ color: 0xff493d, emissive: 0xff160d, emissiveIntensity: 2.8 }),
      ),
      starboardLight: this.track(
        new THREE.MeshStandardMaterial({ color: 0x52ef99, emissive: 0x0cdb67, emissiveIntensity: 2.8 }),
      ),
    };
  }

  private buildHull(): void {
    this.buildFuselage();
    this.buildCanopy();

    if (this.def.aero.wingArea > 0) {
      this.buildWings();
      this.buildWingUtilityPanels();
    } else {
      this.buildCargoShoulders();
    }

    this.buildTurbineNacelles();
    this.buildSurfaceDetails();
    this.buildLandingGear();
  }

  private buildFuselage(): void {
    const [sx, sy, sz] = this.def.size;
    const rings: HullRing[] = [
      { z: -sz * 0.5, radiusX: sx * 0.22, radiusY: sy * 0.24 },
      { z: -sz * 0.44, radiusX: sx * 0.35, radiusY: sy * 0.37 },
      { z: -sz * 0.28, radiusX: sx * 0.46, radiusY: sy * 0.45 },
      { z: sz * 0.08, radiusX: sx * 0.5, radiusY: sy * 0.5 },
      { z: sz * 0.27, radiusX: sx * 0.43, radiusY: sy * 0.46 },
      { z: sz * 0.42, radiusX: sx * 0.27, radiusY: sy * 0.31 },
      { z: sz * 0.5, radiusX: sx * 0.045, radiusY: sy * 0.055 },
    ];
    const hull = new THREE.Mesh(
      this.track(buildHullGeometry(rings, 32)),
      [this.materials.hull, this.materials.heatshield],
    );
    hull.name = 'ship-hull';
    this.root.add(hull);

    // A compact dark engine deck closes the tail without recreating the old,
    // enormous circular capsule cap.
    const aftCap = new THREE.Mesh(
      this.track(new THREE.CylinderGeometry(1, 1, Math.max(0.08, sz * 0.008), 32)),
      this.materials.structure,
    );
    aftCap.name = 'aft-engine-deck';
    aftCap.rotation.x = Math.PI / 2;
    aftCap.position.z = -sz * 0.502;
    aftCap.scale.set(sx * 0.22, 1, sy * 0.24);
    this.root.add(aftCap);
  }

  private buildCanopy(): void {
    const [sx, sy, sz] = this.def.size;
    const canopyX = sx * 0.2;
    const canopyY = sy * 0.2;
    const canopyZ = sz * 0.15;
    const canopyCenter = new THREE.Vector3(0, sy * 0.34, sz * 0.2);

    const canopy = new THREE.Mesh(
      this.track(new THREE.SphereGeometry(1, 28, 12, 0, Math.PI * 2, 0, Math.PI / 2)),
      this.materials.canopy,
    );
    canopy.name = 'cockpit-canopy';
    canopy.position.copy(canopyCenter);
    canopy.scale.set(canopyX, canopyY, canopyZ);
    this.root.add(canopy);

    const rim = new THREE.Mesh(
      this.track(new THREE.TorusGeometry(1, 0.035, 6, 32)),
      this.materials.structure,
    );
    rim.name = 'canopy-rim';
    rim.rotation.x = Math.PI / 2;
    rim.position.copy(canopyCenter);
    rim.scale.set(canopyX, canopyZ, 1);
    this.root.add(rim);

    const spine = new THREE.Mesh(
      this.track(new THREE.BoxGeometry(sx * 0.055, sy * 0.05, canopyZ * 1.9)),
      this.materials.structure,
    );
    spine.name = 'canopy-spine';
    spine.position.set(0, canopyCenter.y + canopyY * 0.88, canopyCenter.z);
    this.root.add(spine);
  }

  private wingHalfSpan(): number {
    const [sx] = this.def.size;
    return Math.max(sx * 0.72, Math.sqrt(this.def.aero.wingArea) * 1.02);
  }

  private wingRootX(): number {
    return this.def.size[0] * 0.32;
  }

  private buildWings(): void {
    const [, sy, sz] = this.def.size;
    const rootX = this.wingRootX();
    const tipX = this.wingHalfSpan();

    for (const side of [-1, 1] as const) {
      const wing = new THREE.Mesh(
        this.track(buildWingGeometry(side, rootX, tipX, sy, sz)),
        [this.materials.hull, this.materials.heatshield, this.materials.structure],
      );
      wing.name = side > 0 ? 'port-wing' : 'starboard-wing';
      this.root.add(wing);

      const tipLight = new THREE.Mesh(
        this.track(new THREE.SphereGeometry(Math.max(0.07, sy * 0.025), 12, 8)),
        side > 0 ? this.materials.portLight : this.materials.starboardLight,
      );
      tipLight.name = side > 0 ? 'port-navigation-light' : 'starboard-navigation-light';
      tipLight.position.set(side * tipX * 0.97, -sy * 0.07, -sz * 0.22);
      this.root.add(tipLight);
    }
  }

  private buildWingUtilityPanels(): void {
    const [, sy, sz] = this.def.size;
    const rootX = this.wingRootX();
    const halfSpan = this.wingHalfSpan();
    const panelWidth = Math.max(0.5, (halfSpan - rootX) * 0.52);
    const panelLength = sz * 0.15;
    const panelX = rootX + (halfSpan - rootX) * 0.48;

    for (const side of [-1, 1] as const) {
      const panel = new THREE.Mesh(
        this.track(new THREE.BoxGeometry(panelWidth, Math.max(0.025, sy * 0.012), panelLength)),
        this.materials.radiator,
      );
      panel.name = side > 0 ? 'port-utility-panel' : 'starboard-utility-panel';
      panel.position.set(side * panelX, -sy * 0.047, -sz * 0.16);
      this.root.add(panel);

      // Flush ribs give the otherwise featureless dark panel a readable scale.
      for (let i = -1; i <= 1; i++) {
        const rib = new THREE.Mesh(
          this.track(new THREE.BoxGeometry(panelWidth * 0.92, sy * 0.014, Math.max(0.025, sz * 0.006))),
          this.materials.structure,
        );
        rib.position.set(side * panelX, -sy * 0.038, -sz * 0.16 + i * panelLength * 0.28);
        this.root.add(rib);
      }
    }
  }

  private buildCargoShoulders(): void {
    const [sx, sy, sz] = this.def.size;
    for (const side of [-1, 1] as const) {
      const pod = new THREE.Mesh(
        this.track(new THREE.BoxGeometry(sx * 0.28, sy * 0.42, sz * 0.52, 1, 1, 4)),
        [this.materials.hull, this.materials.structure],
      );
      pod.name = side > 0 ? 'port-cargo-shoulder' : 'starboard-cargo-shoulder';
      pod.position.set(side * sx * 0.47, -sy * 0.05, -sz * 0.02);
      this.root.add(pod);

      const rail = new THREE.Mesh(
        this.track(new THREE.BoxGeometry(sx * 0.055, sy * 0.48, sz * 0.58)),
        this.materials.structure,
      );
      rail.position.set(side * sx * 0.34, -sy * 0.05, -sz * 0.03);
      this.root.add(rail);
    }
  }

  private buildTurbineNacelles(): void {
    const [sx, sy, sz] = this.def.size;
    const turbines = this.def.engines.filter((engine) => engine.type === 'turbine');
    for (const engine of turbines) {
      const nozzle = this.visualEnginePosition(engine);
      const length = sz * 0.3;
      const radius = Math.min(sx, sy) * 0.12;
      const centerZ = nozzle.z + length * 0.46;

      const nacelle = new THREE.Mesh(
        this.track(new THREE.CapsuleGeometry(radius, Math.max(0.2, length - radius * 2), 6, 18)),
        this.materials.hull,
      );
      nacelle.name = `${engine.id}-nacelle`;
      nacelle.rotation.x = Math.PI / 2;
      nacelle.position.set(nozzle.x, nozzle.y - sy * 0.03, centerZ);
      this.root.add(nacelle);

      const intake = new THREE.Mesh(
        this.track(new THREE.CylinderGeometry(radius * 0.72, radius * 0.92, Math.max(0.12, length * 0.045), 20)),
        this.materials.intake,
      );
      intake.name = `${engine.id}-intake`;
      intake.rotation.x = Math.PI / 2;
      intake.position.set(nozzle.x, nozzle.y - sy * 0.03, centerZ + length * 0.48);
      this.root.add(intake);

      const pylonWidth = Math.max(0.12, Math.abs(nozzle.x) - sx * 0.27);
      const pylon = new THREE.Mesh(
        this.track(new THREE.BoxGeometry(pylonWidth, sy * 0.13, sz * 0.18)),
        this.materials.structure,
      );
      pylon.name = `${engine.id}-pylon`;
      pylon.position.set(nozzle.x * 0.68, nozzle.y, centerZ - length * 0.02);
      this.root.add(pylon);
    }
  }

  private buildSurfaceDetails(): void {
    const [sx, sy, sz] = this.def.size;
    const seams = [
      { z: -sz * 0.29, rx: sx * 0.455, ry: sy * 0.445 },
      { z: sz * 0.03, rx: sx * 0.495, ry: sy * 0.495 },
      { z: sz * 0.27, rx: sx * 0.43, ry: sy * 0.46 },
    ];
    for (const seamDef of seams) {
      const seam = new THREE.Mesh(
        this.track(new THREE.TorusGeometry(1, 0.016, 5, 36)),
        this.materials.structure,
      );
      seam.name = 'hull-frame-ring';
      seam.position.z = seamDef.z;
      seam.scale.set(seamDef.rx, seamDef.ry, 1);
      this.root.add(seam);
    }

    const dorsalHousing = new THREE.Mesh(
      this.track(new THREE.BoxGeometry(sx * 0.14, sy * 0.12, sz * 0.24)),
      this.materials.structure,
    );
    dorsalHousing.name = 'dorsal-systems-housing';
    dorsalHousing.position.set(0, sy * 0.47, -sz * 0.13);
    this.root.add(dorsalHousing);

    const sensor = new THREE.Mesh(
      this.track(new THREE.CylinderGeometry(sy * 0.045, sy * 0.055, Math.max(0.08, sz * 0.012), 16)),
      this.materials.landingLight,
    );
    sensor.name = 'nose-sensor';
    sensor.rotation.x = Math.PI / 2;
    sensor.position.set(0, 0, sz * 0.503);
    this.root.add(sensor);

    for (const side of [-1, 1] as const) {
      const light = new THREE.Mesh(
        this.track(new THREE.SphereGeometry(Math.max(0.065, sy * 0.022), 12, 8)),
        this.materials.landingLight,
      );
      light.name = 'forward-landing-light';
      light.position.set(side * sx * 0.12, -sy * 0.27, sz * 0.37);
      this.root.add(light);
    }

    const accent = new THREE.Mesh(
      this.track(new THREE.BoxGeometry(sx * 0.055, sy * 0.035, sz * 0.27)),
      this.materials.accent,
    );
    accent.name = 'dorsal-service-stripe';
    accent.position.set(sx * 0.14, sy * 0.49, -sz * 0.13);
    this.root.add(accent);
  }

  private buildLandingGear(): void {
    const [, sy] = this.def.size;
    const strutRadius = Math.max(0.075, sy * 0.025);
    for (let i = 0; i < this.def.landingGear.contacts.length; i++) {
      const contact = this.def.landingGear.contacts[i];
      const foot = new THREE.Vector3(contact[0], contact[1] + 0.14, contact[2]);
      const attach = new THREE.Vector3(contact[0] * 0.58, -sy * 0.27, contact[2] * 0.76);
      const braceAttach = new THREE.Vector3(contact[0] * 0.42, -sy * 0.13, contact[2] * 0.54);
      this.addStrut(attach, foot, strutRadius, `landing-gear-${i}-main`);
      this.addStrut(braceAttach, foot, strutRadius * 0.62, `landing-gear-${i}-brace`);

      const pad = new THREE.Mesh(
        this.track(new THREE.CylinderGeometry(sy * 0.085, sy * 0.11, 0.14, 12)),
        this.materials.structure,
      );
      pad.name = `landing-pad-${i}`;
      pad.position.set(contact[0], contact[1] + 0.07, contact[2]);
      this.root.add(pad);
    }
  }

  private addStrut(start: THREE.Vector3, end: THREE.Vector3, radius: number, name: string): void {
    const delta = new THREE.Vector3().subVectors(end, start);
    const strut = new THREE.Mesh(
      this.track(new THREE.CylinderGeometry(radius * 0.82, radius, delta.length(), 8)),
      this.materials.structure,
    );
    strut.name = name;
    strut.position.copy(start).add(end).multiplyScalar(0.5);
    orientAlong(strut, delta);
    this.root.add(strut);
  }

  private visualEnginePosition(engine: EngineDef): THREE.Vector3 {
    // Turbine force is applied near each nacelle's centre in the simulation,
    // but the visible nozzle belongs at the aft end of that nacelle.
    const axialEngine =
      engine.type !== 'rcs' && engine.type !== 'landing' && Math.abs(engine.direction[2]) > 0.7;
    const z = engine.type === 'turbine'
      ? -this.def.size[2] * 0.35
      : axialEngine
        ? -this.def.size[2] * 0.505
        : engine.position[2];
    return new THREE.Vector3(engine.position[0], engine.position[1], z);
  }

  private buildEngines(): void {
    for (const engineDef of this.def.engines) {
      const isRcs = engineDef.type === 'rcs';
      const thrustScale = Math.cbrt(Math.max(1, engineDef.maxThrust) / 40000);
      const nozzleRadius = isRcs
        ? Math.max(0.09, Math.min(...this.def.size) * 0.025)
        : Math.min(Math.min(...this.def.size) * 0.16, Math.max(0.24, thrustScale * 0.4));
      const nozzleLength = isRcs ? nozzleRadius * 0.45 : nozzleRadius * 1.35;
      const baseDirection = new Vec3(engineDef.direction[0], engineDef.direction[1], engineDef.direction[2]);
      const mount = this.visualEnginePosition(engineDef);

      if (!isRcs) {
        const bell = new THREE.Mesh(
          this.track(new THREE.CylinderGeometry(nozzleRadius * 0.54, nozzleRadius, nozzleLength, 16, 2, true)),
          this.materials.nozzle,
        );
        bell.name = `${engineDef.id}-engine-bell`;
        bell.position.copy(mount);
        orientAlong(bell, baseDirection);
        this.root.add(bell);

        const collar = new THREE.Mesh(
          this.track(new THREE.CylinderGeometry(nozzleRadius * 1.03, nozzleRadius * 1.03, nozzleLength * 0.14, 16)),
          this.materials.structure,
        );
        collar.name = `${engineDef.id}-engine-collar`;
        collar.position.copy(mount).addScaledVector(
          new THREE.Vector3(baseDirection.x, baseDirection.y, baseDirection.z).normalize(),
          nozzleLength * 0.38,
        );
        orientAlong(collar, baseDirection);
        this.root.add(collar);
      }

      const plumeLength = isRcs ? nozzleRadius * 5 : nozzleRadius * 6.2;
      const plumeGeometry = this.track(
        new THREE.ConeGeometry(nozzleRadius * (isRcs ? 0.55 : 0.72), plumeLength, isRcs ? 6 : 12, 1, true),
      );
      // ConeGeometry's apex is at +Y. Move that apex to the mesh origin so
      // throttle scaling never makes the plume detach from its nozzle.
      plumeGeometry.translate(0, -plumeLength * 0.5, 0);
      const plumeMaterial = this.track(
        new THREE.MeshBasicMaterial({
          color: plumeColorFor(engineDef.type),
          transparent: true,
          opacity: 0,
          depthWrite: false,
          blending: THREE.AdditiveBlending,
          side: THREE.DoubleSide,
        }),
      );
      const plume = new THREE.Mesh(plumeGeometry, plumeMaterial);
      plume.name = `${engineDef.id}-plume`;
      plume.position.copy(mount).addScaledVector(
        new THREE.Vector3(baseDirection.x, baseDirection.y, baseDirection.z).normalize(),
        -nozzleLength * 0.52,
      );
      orientAlong(plume, baseDirection);
      plume.visible = false;
      plume.renderOrder = 50;
      this.root.add(plume);

      this.plumes.set(engineDef.id, plume);
      this.plumeMaterials.set(engineDef.id, plumeMaterial);
    }
  }

  private buildFieldGlow(): void {
    if (!this.def.fieldDrive) return;
    const material = this.track(
      new THREE.MeshBasicMaterial({
        color: 0x6fd8e8,
        transparent: true,
        opacity: 0,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        side: THREE.BackSide,
      }),
    );
    const radius = Math.max(...this.def.size) * 0.8;
    this.fieldGlow = new THREE.Mesh(this.track(new THREE.SphereGeometry(radius, 24, 16)), material);
    this.fieldGlow.name = 'field-drive-glow';
    this.fieldGlow.renderOrder = 60;
    this.root.add(this.fieldGlow);
  }

  /** Updates every visual effect from the current simulation state. */
  update(engines: readonly Engine[], fieldDriveOutput: number, atmosphericDensity: number): void {
    for (const engine of engines) {
      const plume = this.plumes.get(engine.id);
      const material = this.plumeMaterials.get(engine.id);
      if (!plume || !material) continue;

      const throttle = engine.throttle * Math.min(1, engine.powerSatisfaction);
      if (throttle <= 0.01 || engine.lastThrust <= 0) {
        plume.visible = false;
        continue;
      }

      plume.visible = true;
      const vacuumExpansion = 1 + (1 - Math.min(1, atmosphericDensity / 0.6)) * 0.7;
      const width = (0.55 + throttle * 0.45) * vacuumExpansion;
      plume.scale.set(width, Math.max(0.12, throttle), width);
      material.opacity = 0.28 + throttle * 0.62;
      orientAlong(plume, engine.thrustDirection(this.thrustDirectionScratch));
    }

    if (this.fieldGlow) {
      const glowMaterial = this.fieldGlow.material as THREE.MeshBasicMaterial;
      glowMaterial.opacity = Math.min(0.32, fieldDriveOutput * 0.3);
      this.fieldGlow.visible = glowMaterial.opacity > 0.005;
    }
  }

  dispose(): void {
    for (const disposable of this.disposables) disposable.dispose();
    this.disposables.length = 0;
    this.plumes.clear();
    this.plumeMaterials.clear();
  }
}

/**
 * Builds an elliptical lifting body. Material 0 covers the upper/sides and
 * material 1 covers the ventral heat shield.
 */
export function buildHullGeometry(rings: readonly HullRing[], radialSegments: number): THREE.BufferGeometry {
  const positions: number[] = [];
  const uvs: number[] = [];
  const upperIndices: number[] = [];
  const lowerIndices: number[] = [];

  for (let ringIndex = 0; ringIndex < rings.length; ringIndex++) {
    const ring = rings[ringIndex];
    const v = ringIndex / (rings.length - 1);
    for (let segment = 0; segment <= radialSegments; segment++) {
      const u = segment / radialSegments;
      const angle = u * Math.PI * 2;
      positions.push(Math.cos(angle) * ring.radiusX, Math.sin(angle) * ring.radiusY, ring.z);
      uvs.push(u, v);
    }
  }

  const stride = radialSegments + 1;
  for (let ringIndex = 0; ringIndex < rings.length - 1; ringIndex++) {
    for (let segment = 0; segment < radialSegments; segment++) {
      const a = ringIndex * stride + segment;
      const b = a + stride;
      const c = b + 1;
      const d = a + 1;
      const midAngle = ((segment + 0.5) / radialSegments) * Math.PI * 2;
      const target = Math.sin(midAngle) < -0.2 ? lowerIndices : upperIndices;
      target.push(a, d, b, b, d, c);
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setIndex([...upperIndices, ...lowerIndices]);
  geometry.addGroup(0, upperIndices.length, 0);
  geometry.addGroup(upperIndices.length, lowerIndices.length, 1);
  geometry.computeVertexNormals();
  geometry.computeBoundingSphere();
  return geometry;
}

/** Top hull, heat-shield underside and dark edge faces use materials 0/1/2. */
function buildWingGeometry(
  side: -1 | 1,
  rootX: number,
  tipX: number,
  height: number,
  length: number,
): THREE.BufferGeometry {
  const topY = -height * 0.055;
  const bottomY = topY - height * 0.075;
  const rawOutline = [
    new THREE.Vector3(side * rootX, topY, length * 0.2),
    new THREE.Vector3(side * tipX, topY, -length * 0.07),
    new THREE.Vector3(side * tipX * 0.94, topY, -length * 0.29),
    new THREE.Vector3(side * rootX, topY, -length * 0.34),
  ];
  const outline = side > 0 ? rawOutline : [rawOutline[0], rawOutline[3], rawOutline[2], rawOutline[1]];
  const positions: number[] = [];
  const uvs: number[] = [];
  const groups: { start: number; count: number; materialIndex: number }[] = [];

  const pushTriangle = (a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3): void => {
    for (const p of [a, b, c]) {
      positions.push(p.x, p.y, p.z);
      uvs.push(Math.abs(p.x) / tipX, (p.z / length + 0.35) / 0.56);
    }
  };

  const startTop = positions.length / 3;
  pushTriangle(outline[0], outline[1], outline[2]);
  pushTriangle(outline[0], outline[2], outline[3]);
  groups.push({ start: startTop, count: 6, materialIndex: 0 });

  const bottom = outline.map((p) => new THREE.Vector3(p.x, bottomY, p.z));
  const startBottom = positions.length / 3;
  pushTriangle(bottom[0], bottom[2], bottom[1]);
  pushTriangle(bottom[0], bottom[3], bottom[2]);
  groups.push({ start: startBottom, count: 6, materialIndex: 1 });

  const startSides = positions.length / 3;
  for (let i = 0; i < outline.length; i++) {
    const next = (i + 1) % outline.length;
    pushTriangle(outline[i], bottom[next], outline[next]);
    pushTriangle(outline[i], bottom[i], bottom[next]);
  }
  groups.push({ start: startSides, count: 24, materialIndex: 2 });

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  for (const group of groups) geometry.addGroup(group.start, group.count, group.materialIndex);
  geometry.computeVertexNormals();
  geometry.computeBoundingSphere();
  return geometry;
}

const ORIENT_FROM = new THREE.Vector3(0, 1, 0);
const ORIENT_TO = new THREE.Vector3();

/** Points an object's local +Y axis along `direction`. */
function orientAlong(object: THREE.Object3D, direction: Vec3 | THREE.Vector3): void {
  const length = Math.hypot(direction.x, direction.y, direction.z);
  if (length < 1e-9) return;
  ORIENT_TO.set(direction.x / length, direction.y / length, direction.z / length);
  object.quaternion.setFromUnitVectors(ORIENT_FROM, ORIENT_TO);
}

function plumeColorFor(type: string): number {
  switch (type) {
    case 'nuclearThermal':
      return 0x9fd8ff;
    case 'ion':
      return 0x9f7fe0;
    case 'fusion':
      return 0xd0f0ff;
    case 'turbine':
      return 0xffb060;
    case 'rcs':
      return 0xd8e8ff;
    case 'landing':
      return 0xffc890;
    default:
      return 0xffa050;
  }
}
